import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PFlag, PHeading, PIcon, PTag, PText } from "@porsche-design-system/components-react"
import type { FlagName } from "@porsche-design-system/components-react"
import { breakpoint, spacingStaticMd, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { VehicleHistoryTeaser } from "@slfinrtl/ui-bff-routes"
import { VehicleStatus } from "@slfinrtl/ui-bff-routes"
import { defineMessages, FormattedMessage } from "react-intl"
import { Link } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { placeholderImageUrl } from "src/common/placeholderImageUrl.const"
import { getDealerAppRoute } from "src/common/routes"
import { getDeliveredVehicleDetailsUrlForMarketplace } from "src/common/routes/getDeliveredVehicleDetailsUrlForMarketplace"
import { PictureResponsive } from "src/components/pictureResponsive/PictureResponsive"
import { StatusBadge } from "src/components/statusBadge/StatusBadge"

const messages = defineMessages({
	noVehicleFoundHeadline: {
		defaultMessage: "No active vehicle found",
		id: "vehicleHistory.header.noVehicleFound.headline",
	},
	noVehicleFoundMessage: {
		defaultMessage: "The VIN you have entered is currently not part of any inventory.",
		id: "vehicleHistory.header.noVehicleFound.message",
	},
	pcssLink: {
		defaultMessage: "PCSS",
		id: "vehicleHistory.header.pcssLink",
	},
	vehicleDetailsLink: {
		defaultMessage: "Vehicle Details",
		id: "vehicleHistory.header.vehicleDetailsLink",
	},
})

interface VehicleHistoryHeaderProps {
	teaser: VehicleHistoryTeaser | null
}

export const VehicleHistoryHeader = ({ teaser }: VehicleHistoryHeaderProps) => {
	const { locale } = useDealerContext()
	const routes = useDealerAppRoutes()

	// A delivered vehicle no longer lives in inventory, so link to its delivered-vehicles details instead.
	// The vehicle can belong to another marketplace than the current one, so its routes are resolved from that marketplace.
	const vehicleDetailsUrl =
		teaser?.statusBadge?.status === VehicleStatus.DELIVERED && teaser.marketplace
			? getDeliveredVehicleDetailsUrlForMarketplace(teaser.marketplace)
			: routes?.inventoryList?.vehicleDetails?.url

	return teaser !== null ? (
		<Header>
			{teaser.image && (
				<StyledPictureResponsive
					picture={teaser.image}
					alt={teaser.title}
					sizes={{ base: "full", m: "one-eighth", s: "half" }}
				/>
			)}

			<HeadingTagsWrapper>
				<PHeading size="xl" tag="h1">
					{teaser.title}
				</PHeading>

				<TagsContainer>
					{teaser.statusBadge && <StatusBadge status={teaser.statusBadge.status} autoWidth />}
					{teaser.vehicleType && <PTag>{teaser.vehicleType}</PTag>}
					{teaser.mileage && <PTag>{teaser.mileage}</PTag>}
					{teaser.inventoryId && teaser.dealershipId && vehicleDetailsUrl && (
						<PTag>
							<Link
								to={`${getDealerAppRoute(teaser.dealershipId, locale)}${vehicleDetailsUrl(teaser.inventoryId)}`}
							>
								<FormattedMessage {...messages.vehicleDetailsLink} />
							</Link>
						</PTag>
					)}
					{teaser.vin && routes?.external && (
						<PTag>
							<a href={routes.external?.pcss.url(teaser.vin)} target="_blank" rel="noreferrer">
								<FormattedMessage {...messages.pcssLink} />
								<TagIcon size="xs" name="external" />
							</a>
						</PTag>
					)}
					{teaser.dealershipName && <PTag icon="pin">{teaser.dealershipName}</PTag>}
					{teaser.marketplace && <PFlag name={teaser.marketplace.toLowerCase() as FlagName} />}
				</TagsContainer>
			</HeadingTagsWrapper>
		</Header>
	) : (
		<Header>
			<StyledPictureResponsive
				picture={{
					aspectRatio: 4 / 3,
					variants: [{ height: 720, mimeType: "image/jpeg", url: placeholderImageUrl, width: 960 }],
				}}
				sizes={{ base: "full", m: "one-eighth", s: "half" }}
			/>
			<div>
				<PHeading size="xl" tag="h1">
					<FormattedMessage {...messages.noVehicleFoundHeadline} />
				</PHeading>
				<PText>
					<FormattedMessage {...messages.noVehicleFoundMessage} />
				</PText>
			</div>
		</Header>
	)
}

const Header = styled.header`
    display: flex;
    flex-direction: column;
    gap: ${spacingStaticSm};

    @media (min-width: ${breakpoint.m}px) {
        flex-direction: row;
        align-items: center;
        gap: ${spacingStaticMd};
    }
`

const HeadingTagsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
`

const StyledPictureResponsive = styled(PictureResponsive)`
    @media (min-width: ${breakpoint.m}px) {
        min-width: 160px;
        width: 160px;
    }
`

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
	align-items: center;
    gap: ${spacingStaticSm};
`

const TagIcon = styled(PIcon)`
	margin-inline-start: 2px;
`
